export type GalleryAssetSource = 'persisted' | 'pending';

export interface GalleryAssetMetadata {
  wineryId: string;
  entityId?: string;
  entityType?: string;
  fileName?: string;
  displayOrder?: number;
  flags?: string[];
}

export interface PromotedGalleryAsset {
  storePublicId: string;
  storeRelativePath: string;
  secureUrl: string;
  width?: number;
  height?: number;
}

export interface PendingGalleryAsset {
  tempPublicId: string;
  tempSecureUrl: string;
  file?: File | Blob;
  name: string;
  type: 'image' | 'video' | 'pdf' | 'gif' | 'attachment';
  flags?: string[];
  displayOrder?: number;
  widthPx?: number;
  heightPx?: number;
  fileSizeBytes?: number;
}

const PRODUCTION_PREFIX = 'production';

export function tempFolder(wineryId: string): string {
  return wineryId
    ? `${PRODUCTION_PREFIX}/temp/${wineryId}`
    : `${PRODUCTION_PREFIX}/temp`;
}

export function storeFolder(wineryId: string): string {
  return wineryId
    ? `${PRODUCTION_PREFIX}/store/${wineryId}`
    : `${PRODUCTION_PREFIX}/store`;
}

/**
 * Converts a Cloudinary public_id under production/store/ to the DB-relative store path.
 */
export function toStoreDbPath(publicId: string, wineryId?: string): string {
  let relative = publicId;
  if (relative.startsWith('production/store/')) {
    relative = relative.replace('production/store/', '');
  }
  if (relative.startsWith('store/')) {
    relative = relative.replace('store/', '');
  }
  // Shrine flat id (e.g. 721222….png) — keep as-is until backend relocate adds winery prefix
  if (wineryId && relative.startsWith(`${wineryId}/`)) {
    return relative;
  }
  return relative;
}

/** Expected Cloudinary public_id after temp → store promote (same basename, winery-scoped folder). */
export function expectedStorePublicId(tempPublicId: string, wineryId: string): string {
  const basename = tempPublicId.substring(tempPublicId.lastIndexOf('/') + 1);
  return `${storeFolder(wineryId)}/${basename}`;
}

export function isStorePublicId(publicId: string): boolean {
  return (
    publicId.startsWith('store/') ||
    publicId.startsWith('production/store/')
  );
}

export function mergeGalleryItemsByDisplayOrder<T extends { displayOrder?: number }>(
  persisted: T[],
  pending: T[]
): T[] {
  return [...persisted, ...pending].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );
}

export function buildGalleryAssetBaseName(fileName?: string): string {
  const timestamp = Date.now();
  const stem = (fileName || 'upload')
    .replace(/\.[^/.]+$/, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .slice(0, 80);
  const suffix = Math.random().toString(36).slice(2, 8);
  return `${timestamp}_${stem}_${suffix}`;
}

/** Full Cloudinary public_id including folder prefix, e.g. production/temp/{wineryId}/... */
export function buildGalleryAssetPublicId(folder: string, fileName?: string): string {
  return `${folder}/${buildGalleryAssetBaseName(fileName)}`;
}

export function basenameFromPublicId(publicId: string): string {
  const name = publicId.split('/').pop() || publicId;
  return name.replace(/\.[^/.]+$/, '');
}
